import React, { useRef, useState } from 'react';
import ModalSheet from "../../../../components/atoms/modalSheet";
import Button from "../../../../components/atoms/button";
import { IonItem, IonSelect, IonSelectOption, IonList, IonInput } from "@ionic/react";
import { PointLogosDTO } from "../../../../services/models/pointLogoDTO";
import { getPolygonCenterPoint } from "../../../../services/genericMethoud/gisMethouds";
import { convertToBase64 } from "../../../../services/genericMethoud/generice";
import './LogoPointsModal.css';
import { onFileUploadValidationSize } from '../../../../services/API';

interface LogoPointsModalProps {
  isOpen: boolean;
  onClose: () => void;
  getLogosData: any[];
  parcel_Cod: string;
  logoId: string;
  token: string;
  isOperation: boolean;
  logoPointsTypeValue: string;
  setLogoPointsTypeValue: (value: string) => void;
  onDeleteLogo: (logoId: string) => void;
  onAddLogo: (model: any) => void;
  inputClassWithOutError: string;
  inputClassWithError: string;
  property?: any;
}

const LogoPointsModal: React.FC<LogoPointsModalProps> = ({
  isOpen,
  onClose,
  getLogosData,
  parcel_Cod,
  logoId,
  token,
  isOperation,
  logoPointsTypeValue,
  setLogoPointsTypeValue,
  onDeleteLogo,
  onAddLogo,
  inputClassWithOutError,
  inputClassWithError,
  property
}) => {
  const [selectedFile, setSelectedFile] = React.useState<File | null>(null);
  const [logoFile, setLogoFile] = React.useState<any>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string>("");

  // Refs for additional input fields
  const inputLogoNameRef = useRef<HTMLIonInputElement>(null);
  const inputLogoDescriptionRef = useRef<HTMLIonInputElement>(null);
  const inputLogoWidthRef = useRef<HTMLIonInputElement>(null);
  const inputLogoHeightRef = useRef<HTMLIonInputElement>(null);

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.files && event.target.files[0]) {
      setSelectedFile(event.target.files[0]);
      setErrorMessage(""); // Clear error when file is selected
    }
  };

  const handleLogoFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.files && event.target.files[0]) {
      setLogoFile(event.target.files);
      setErrorMessage(""); // Clear error when file is selected
    }
  };

  const clearForm = () => {
    setSelectedFile(null);
    setLogoFile(null);
    setErrorMessage("");
    if (inputLogoNameRef.current) inputLogoNameRef.current.value = "";
    if (inputLogoDescriptionRef.current) inputLogoDescriptionRef.current.value = "";
    if (inputLogoWidthRef.current) inputLogoWidthRef.current.value = "";
    if (inputLogoHeightRef.current) inputLogoHeightRef.current.value = "";
  };

  const handleAddLogo = async () => {
    if (!logoPointsTypeValue || !property) {
      setErrorMessage("يرجى اختيار نوع اللوجو");
      return;
    }

    setIsLoading(true);
    setErrorMessage("");

    try {
      let model = new PointLogosDTO();
      const centroid = property?.geometry ? getPolygonCenterPoint(property.geometry) : null;

      model.longitude = centroid?.longitude?.toString() || "";
      model.latitude = centroid?.latitude?.toString() || "";
      model.parcelObjId = parcel_Cod;
      model.logoFileName = logoPointsTypeValue;
      model.isActive = true;
      model.type = logoPointsTypeValue;
      model.zoomLevel = 18;

      if (logoPointsTypeValue === "logoAdd") {
        // Handle custom logo with additional fields
        if (!logoFile) {
          setErrorMessage("يرجى اختيار ملف اللوجو");
          setIsLoading(false);
          return;
        }

        if (!inputLogoDescriptionRef.current?.value) {
          setErrorMessage("يرجى إدخال وصف اللوجو");
          setIsLoading(false);
          return;
        }

        if (!inputLogoNameRef.current?.value) {
          setErrorMessage("يرجى إدخال اسم اللوجو");
          setIsLoading(false);
          return;
        }

        const width = Number(inputLogoWidthRef.current?.value);
        const height = Number(inputLogoHeightRef.current?.value);

        if (!width || !height || width <= 0 || height <= 0) {
          setErrorMessage("يرجى إدخال أبعاد صحيحة للوجو");
          setIsLoading(false);
          return;
        }

        model.width = width;
        model.height = height;
        model.logoName = inputLogoNameRef.current?.value?.toString();
        model.description = inputLogoDescriptionRef.current?.value?.toString();

        // Validate file size
        let checkFileResult = await onFileUploadValidationSize(logoFile[0], 10 * 5000);
        if (!checkFileResult) {
          setErrorMessage(`عفوا الملف ${logoFile[0].name} حجمه أكبر من 5 كيلوبايت`);
          setIsLoading(false);
          return;
        }

        // Convert file to base64
        let files: any = await convertToBase64(logoFile[0]);
        let regex = /data:.*base64,/;
        let fileWithOut = files.replace(regex, "");
        model.LogoFile = fileWithOut;
      } else {
        // Handle predefined logo types
        if (!selectedFile) {
          setErrorMessage("يرجى اختيار صورة اللوجو");
          setIsLoading(false);
          return;
        }

        model.width = Number(inputLogoWidthRef.current?.value);
        model.height = Number(inputLogoHeightRef.current?.value);

        // Convert file to base64
        const reader = new FileReader();
        reader.onload = () => {
          const base64String = reader.result as string;
          const regex = /data:.*base64,/;
          const fileWithoutPrefix = base64String.replace(regex, "");
          model.LogoFile = fileWithoutPrefix;

          onAddLogo(model);
          setSelectedFile(null);
          clearForm();
        };
        reader.readAsDataURL(selectedFile);
        setIsLoading(false);
        return;
      }

      // Call onAddLogo for logoAdd type
      onAddLogo(model);
      clearForm();

    } catch (error) {
      setErrorMessage("حدث خطأ أثناء معالجة الملف");
      console.error("Error in handleAddLogo:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const existingLogo = () => {
    return getLogosData?.find((i: any) => i.parcelObjId == parcel_Cod || i.id == logoId);
  };

  return (
    <ModalSheet
      isOpen={isOpen}
      initialBreakpoint={0.9}
      breakpoints={[1, 1, 1, 1]}
      onDidDismiss={onClose}
      close={onClose}
    >
      <div className="w-full h-full pt-4 flex flex-col justify-start items-center">
        <p className="font-bold text-primaryColor100 text-2xl animate__animated animate__bounceInDown">
          اعدادات اللوجو والشعارات
        </p>

        {/* Error Message Display */}
        {errorMessage && (
          <div className="w-full mt-4 px-4 animate__animated animate__shakeX">
            <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded">
              {errorMessage}
            </div>
          </div>
        )}

        {existingLogo() ? (
          <>
            <div className="w-full mt-4 animate__animated animate__bounceInDown">
              <Button
                theme={"danger"}
                text={"حذف اللوجو"}
                size={"big"}
                onClick={() => onDeleteLogo(existingLogo()?.id)}
              />
            </div>
          </>
        ) : (
          <>
            <div className="relative w-full mt-4 animate__animated animate__bounceInLeft">
              <IonList
                className={
                  logoPointsTypeValue
                    ? `${inputClassWithOutError}`
                    : `${inputClassWithError}`
                }
              >
                {isOperation ? (
                  <IonItem>
                    <IonSelect
                      interface="action-sheet"
                      placeholder="يرجى الاختيار"
                      onIonChange={(e: any) => {
                        setLogoPointsTypeValue(e.target.value);
                        setErrorMessage(""); // Clear error when selection changes
                      }}
                    >
                      <IonSelectOption value="hospital">
                        مستشفي
                      </IonSelectOption>
                      <IonSelectOption value="mosque">مسجد</IonSelectOption>
                      <IonSelectOption value="school">
                        مدرسة أولاد
                      </IonSelectOption>
                      <IonSelectOption value="girlSchool">
                        مدرسة بنات
                      </IonSelectOption>
                      <IonSelectOption value="beqala">
                        بقالة
                      </IonSelectOption>
                    </IonSelect>
                  </IonItem>
                ) : (
                  <IonItem>
                    <IonSelect
                      interface="action-sheet"
                      placeholder="يرجى الاختيار"
                      onIonChange={(e: any) => {
                        setLogoPointsTypeValue(e.target.value);
                        setErrorMessage(""); // Clear error when selection changes
                      }}
                    >
                      <IonSelectOption value="hospital">
                        مستشفي
                      </IonSelectOption>
                      <IonSelectOption value="mosque">مسجد</IonSelectOption>
                      <IonSelectOption value="school">
                        مدرسة أولاد
                      </IonSelectOption>
                      <IonSelectOption value="girlSchool">
                        مدرسة بنات
                      </IonSelectOption>
                      <IonSelectOption value="pharmacy">
                        صيدلية
                      </IonSelectOption>
                      <IonSelectOption value="restaurant">
                        مطعم
                      </IonSelectOption>
                      <IonSelectOption value="cafe">
                        مقهى
                      </IonSelectOption>
                      <IonSelectOption value="shop">
                        محل
                      </IonSelectOption>
                      <IonSelectOption value="gasStation">
                        محطة بنزين
                      </IonSelectOption>
                      <IonSelectOption value="bank">
                        بنك
                      </IonSelectOption>
                      <IonSelectOption value="hotel">
                        فندق
                      </IonSelectOption>
                      <IonSelectOption value="clinic">
                        عيادة
                      </IonSelectOption>
                      <IonSelectOption value="beqala">
                        بقالة
                      </IonSelectOption>
                      <IonSelectOption value="fireing">
                        محروقات
                      </IonSelectOption>
                      <IonSelectOption value="logoAdd">
                        لوجو دعائي
                      </IonSelectOption>
                      <IonSelectOption value="other">
                        أخرى
                      </IonSelectOption>
                    </IonSelect>
                  </IonItem>
                )}
              </IonList>
              <label className="absolute text-sm font-bold top-2 z-10 bg-white px-2 text-primaryColor100 -translate-y-6 right-10">
                اختيار اللوجو أو الشعار
              </label>
            </div>

            {logoPointsTypeValue === "logoAdd" ? (
              <>
                <div className="relative w-full mt-2 animate__animated animate__bounceInRight">
                  <IonItem>
                    <IonInput
                      type="number"
                      autofocus={true}
                      className={
                        inputLogoWidthRef.current?.value
                          ? `${inputClassWithOutError}`
                          : `${inputClassWithError}`
                      }
                      ref={inputLogoWidthRef}
                      placeholder="العرض"
                    ></IonInput>
                  </IonItem>
                  <label className="absolute text-sm font-bold top-2 z-10 pt-2 bg-white px-2 text-primaryColor100 -translate-y-6 right-10">
                    العرض
                  </label>
                </div>

                <div className="relative w-full mt-2 animate__animated animate__bounceInLeft">
                  <IonItem>
                    <IonInput
                      type="number"
                      autofocus={true}
                      className={
                        inputLogoHeightRef.current?.value
                          ? `${inputClassWithOutError}`
                          : `${inputClassWithError}`
                      }
                      ref={inputLogoHeightRef}
                      placeholder="الطول"
                    ></IonInput>
                  </IonItem>
                  <label className="absolute text-sm font-bold top-2 z-10 pt-2 bg-white px-2 text-primaryColor100 -translate-y-6 right-10">
                    الطول
                  </label>
                </div>

                <div className="relative w-full mt-2 animate__animated animate__bounceInRight">
                  <IonItem>
                    <IonInput
                      type="text"
                      autofocus={true}
                      className={
                        inputLogoNameRef?.current?.value
                          ? `${inputClassWithOutError}`
                          : `${inputClassWithError}`
                      }
                      ref={inputLogoNameRef}
                      placeholder="اسم اللوجو"
                    ></IonInput>
                  </IonItem>
                  <label className="absolute text-sm font-bold top-2 z-10 pt-2 bg-white px-2 text-primaryColor100 -translate-y-6 right-10">
                    اسم اللوجو
                  </label>
                </div>

                <div className="relative w-full mt-2 animate__animated animate__bounceInLeft">
                  <IonItem>
                    <IonInput
                      type="text"
                      autofocus={true}
                      className={
                        inputLogoDescriptionRef?.current?.value
                          ? `${inputClassWithOutError}`
                          : `${inputClassWithError}`
                      }
                      ref={inputLogoDescriptionRef}
                      placeholder="وصف اللوجو"
                    ></IonInput>
                  </IonItem>
                  <label className="absolute text-sm font-bold top-2 z-10 pt-2 bg-white px-2 text-primaryColor100 -translate-y-6 right-10">
                    وصف اللوجو
                  </label>
                </div>

                <div className="w-full animate__animated animate__backInUp">
                  <IonItem>
                    <div className="relative w-full mt-4">
                      <input
                        onChange={handleLogoFileChange}
                        type="file"
                        multiple
                        accept=".svg"
                        className={
                          logoFile
                            ? `${inputClassWithOutError}`
                            : `${inputClassWithError}`
                        }
                        placeholder="الملف"
                      />
                      <label className="absolute text-sm font-bold top-2 z-10 bg-white px-2 text-primaryColor100 -translate-y-6 right-10">
                        يرجى اخيار الملف
                      </label>
                    </div>
                  </IonItem>
                </div>
              </>
            ) : logoPointsTypeValue ? (
              <>
                <div className="relative w-full mt-2 animate__animated animate__bounceInRight">
                  <IonItem>
                    <IonInput
                      type="number"
                      autofocus={true}
                      className={
                        inputLogoWidthRef.current?.value
                          ? `${inputClassWithOutError}`
                          : `${inputClassWithError}`
                      }
                      ref={inputLogoWidthRef}
                      placeholder="العرض"
                    ></IonInput>
                  </IonItem>
                  <label className="absolute text-sm font-bold top-2 z-10 pt-2 bg-white px-2 text-primaryColor100 -translate-y-6 right-10">
                    العرض
                  </label>
                </div>

                <div className="relative w-full mt-2 animate__animated animate__bounceInLeft">
                  <IonItem>
                    <IonInput
                      type="number"
                      autofocus={true}
                      className={
                        inputLogoHeightRef.current?.value
                          ? `${inputClassWithOutError}`
                          : `${inputClassWithError}`
                      }
                      ref={inputLogoHeightRef}
                      placeholder="الطول"
                    ></IonInput>
                  </IonItem>
                  <label className="absolute text-sm font-bold top-2 z-10 pt-2 bg-white px-2 text-primaryColor100 -translate-y-6 right-10">
                    الطول
                  </label>
                </div>
                <div className="relative w-full mt-4 animate__animated animate__bounceInRight">
                  <input
                    type="file"
                    accept="image/*"
                    onChange={handleFileChange}
                    className="w-full border-2 rounded border-paragraphColor text-2xl text-primaryColor100 p-2"
                  />
                  <label className="absolute text-xl font-bold top-2 z-10 bg-white px-2 text-primaryColor100 -translate-y-6 right-10">
                    الصورة
                  </label>
                </div>
              </>
            ) : null}

            <div className="w-full mt-4 px-4 grid grid-cols-2 gap-4 animate__animated animate__backInUp">
              {logoPointsTypeValue ? (
                <Button
                  theme={"primary"}
                  text={isLoading ? "جاري الإضافة..." : "موافق"}
                  size={"big"}
                  onClick={isLoading ? undefined : handleAddLogo}
                />
              ) : null}
              <Button
                theme={"danger"}
                text={"إغلاق"}
                size={"big"}
                onClick={onClose}
              />
            </div>
          </>
        )}

        {existingLogo() && (
          <div className="w-full mt-4 px-4">
            <Button
              theme={"danger"}
              text={"إغلاق"}
              size={"big"}
              onClick={onClose}
            />
          </div>
        )}
      </div>
    </ModalSheet>
  );
};

export default LogoPointsModal;
